import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { MdCheckCircle, MdCancel, MdContentCopy, MdOpenInNew, MdWarning } from 'react-icons/md';
import { SiteEmpresaDto, SiteVeiculoDto, atualizarPublicacao } from '../../services/meuSiteService';

const AZUL = '#1F467E';
const BORDA = '#e2e8f0';
const FUNDO = '#f5f7fa';

const Card = styled.div`
    background: #fff;
    border: 1px solid ${BORDA};
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 20px;
`;

const CardTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 18px 0;
`;

const StatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
`;

const StatusChip = styled.span<{ publicado: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    background: ${p => p.publicado ? '#d1fae5' : '#f1f5f9'};
    color: ${p => p.publicado ? '#065f46' : '#64748b'};
`;

const PublicarBtn = styled.button<{ despublicar?: boolean }>`
    padding: 10px 22px;
    background: ${p => p.despublicar ? '#ef4444' : AZUL};
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover { opacity: 0.88; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const SiteLink = styled.a`
    font-size: 13px;
    color: ${AZUL};
    text-decoration: underline;
    word-break: break-all;
`;

const IconBtn = styled.button`
    background: none;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 6px 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #64748b;
    &:hover { background: ${FUNDO}; }
`;

const WarningBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fef9c3;
    border: 1px solid #fde68a;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 13px;
    color: #78350f;
`;

const ConfirmOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ConfirmBox = styled.div`
    background: #fff;
    border-radius: 10px;
    padding: 28px 32px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
`;

const ConfirmTitle = styled.h3`
    margin: 0 0 10px 0;
    font-size: 16px;
    color: #1e293b;
`;

const ConfirmText = styled.p`
    margin: 0 0 20px 0;
    font-size: 13px;
    color: #64748b;
`;

const ConfirmRow = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
`;

const ConfirmCancelBtn = styled.button`
    padding: 8px 18px;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;
    &:hover { background: ${FUNDO}; }
`;

const ConfirmOkBtn = styled.button`
    padding: 8px 18px;
    border: none;
    border-radius: 6px;
    background: #ef4444;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover { background: #dc2626; }
`;

const ToggleRow = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    user-select: none;
    margin-bottom: 10px;
`;

const ToggleInput = styled.input`
    width: 36px;
    height: 20px;
    cursor: pointer;
    accent-color: ${AZUL};
`;

const AjudaTexto = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
`;

interface Props {
    site: SiteEmpresaDto;
    veiculos: SiteVeiculoDto[];
    onPublicadoChange: (publicado: boolean) => void;
    onExibeAreaClienteChange: (valor: boolean) => Promise<void>;
}

function extrairMensagem(err: any): string {
    return err?.response?.data?.mensagem
        || err?.response?.data?.Mensagem
        || 'Erro ao realizar operação.';
}

export function AbaPublicacao({ site, veiculos, onPublicadoChange, onExibeAreaClienteChange }: Props) {
    const [loading, setLoading] = useState(false);
    const [confirmandoDespublicar, setConfirmandoDespublicar] = useState(false);
    const [salvandoArea, setSalvandoArea] = useState(false);

    const alternarAreaCliente = async (valor: boolean) => {
        setSalvandoArea(true);
        try {
            await onExibeAreaClienteChange(valor);
        } finally {
            setSalvandoArea(false);
        }
    };

    const siteUrl = `https://${site.slug}.locasmartpro.com.br`;
    const nenhumVisivel = veiculos.length === 0 || veiculos.every(v => !v.visivel);

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(siteUrl);
            toast.success('Link copiado.');
        } catch {
            toast.error('Não foi possível copiar o link.');
        }
    };

    const handlePublicar = async () => {
        setLoading(true);
        try {
            await atualizarPublicacao(true);
            onPublicadoChange(true);
            toast.success('Site publicado com sucesso.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDespublicar = async () => {
        setConfirmandoDespublicar(false);
        setLoading(true);
        try {
            await atualizarPublicacao(false);
            onPublicadoChange(false);
            toast.success('Site despublicado.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Card>
                <CardTitle>Estado de publicação</CardTitle>
                <StatusRow>
                    <StatusChip publicado={site.publicado}>
                        {site.publicado ? <MdCheckCircle size={15} /> : <MdCancel size={15} />}
                        {site.publicado ? 'Publicado' : 'Despublicado'}
                    </StatusChip>
                    {site.publicado ? (
                        <PublicarBtn
                            despublicar
                            disabled={loading}
                            onClick={() => setConfirmandoDespublicar(true)}
                        >
                            {loading ? 'Aguarde...' : 'Despublicar site'}
                        </PublicarBtn>
                    ) : (
                        <PublicarBtn disabled={loading} onClick={handlePublicar}>
                            {loading ? 'Aguarde...' : 'Publicar site'}
                        </PublicarBtn>
                    )}
                </StatusRow>

                {nenhumVisivel && (
                    <WarningBox>
                        <MdWarning size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                        Seu site será publicado sem veículos na vitrine. Adicione veículos na aba Veículos e marque pelo menos um como visível.
                    </WarningBox>
                )}
            </Card>

            <Card>
                <CardTitle>Como ver seu site</CardTitle>
                <LinkRow>
                    <SiteLink href={siteUrl} target="_blank" rel="noreferrer">{siteUrl}</SiteLink>
                    <IconBtn type="button" title="Copiar link" onClick={copiar}>
                        <MdContentCopy size={16} />
                    </IconBtn>
                    <IconBtn as="a" href={siteUrl} target="_blank" rel="noreferrer" title="Abrir site">
                        <MdOpenInNew size={16} />
                    </IconBtn>
                </LinkRow>
            </Card>

            <Card>
                <CardTitle>Área do Cliente</CardTitle>
                <ToggleRow>
                    <ToggleInput
                        type="checkbox"
                        checked={site.exibeAreaCliente}
                        disabled={salvandoArea}
                        onChange={e => alternarAreaCliente(e.target.checked)}
                    />
                    Ativar Área do Cliente no site
                </ToggleRow>
                <AjudaTexto>
                    Quando ativa, seus locatários acessam {siteUrl}/area-cliente com o mesmo login do aplicativo para ver locações, pagamentos e documentos.
                </AjudaTexto>
            </Card>

            {confirmandoDespublicar && (
                <ConfirmOverlay>
                    <ConfirmBox>
                        <ConfirmTitle>Despublicar site</ConfirmTitle>
                        <ConfirmText>O site sairá do ar imediatamente. Deseja continuar?</ConfirmText>
                        <ConfirmRow>
                            <ConfirmCancelBtn onClick={() => setConfirmandoDespublicar(false)}>Cancelar</ConfirmCancelBtn>
                            <ConfirmOkBtn onClick={handleDespublicar}>Despublicar</ConfirmOkBtn>
                        </ConfirmRow>
                    </ConfirmBox>
                </ConfirmOverlay>
            )}
        </div>
    );
}
